/**
 * v4.11.0 — Fleet KPI Bar.
 *
 * Five operational metrics rendered as calm dark cards. No bright accents
 * unless needed for status; numbers do the talking. Reuses the platform's
 * card surface utility classes — no custom containers.
 */
import { useTranslation } from "react-i18next";
import {
    Box, CalendarClock, MapPin, Activity, Clock,
} from "lucide-react";

const KpiTile = ({ icon: Icon, accent, label, value, sub, testId, insightId }) => (
    <div
        data-testid={testId}
        data-insight-id={insightId}
        data-insight-label={label}
        className="rounded-xl p-4 sm:p-5 flex items-start gap-3 border"
        style={{
            background: "rgba(15, 23, 42, 0.55)",
            borderColor: "rgba(148, 163, 184, 0.12)",
        }}
    >
        <div
            className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ background: `${accent}1A`, color: accent }}
        >
            <Icon size={16} strokeWidth={1.75} />
        </div>
        <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-wider text-slate-400 mb-0.5 truncate">
                {label}
            </p>
            <p
                className="text-2xl sm:text-3xl font-semibold text-slate-100 leading-none"
                style={{ fontFamily: "Manrope, sans-serif" }}
            >
                {value}
            </p>
            {sub && (
                <p className="text-[11px] text-slate-500 mt-1 truncate">{sub}</p>
            )}
        </div>
    </div>
);

export default function FleetKpiBar({ summary, loading }) {
    const { t } = useTranslation();
    const s = summary || {};
    const fmt = (n) => (n === undefined || n === null) ? "—" : Number(n).toLocaleString();

    return (
        <div
            className="grid gap-3 sm:gap-4 grid-cols-2 md:grid-cols-3 lg:grid-cols-5"
            data-testid="fleet-kpi-bar"
        >
            <KpiTile
                icon={Box}
                accent="#60a5fa"
                label={t("card_fleet.kpi.total_cards")}
                value={loading ? "…" : fmt(s.total_cards)}
                sub={loading ? "" : t("card_fleet.kpi.active_count", { n: fmt(s.active_cards) })}
                testId="kpi-total-cards"
                insightId="fleet.kpi.total-cards"
            />
            <KpiTile
                icon={CalendarClock}
                accent="#a78bfa"
                label={t("card_fleet.kpi.scheduled_cards")}
                value={loading ? "…" : fmt(s.scheduled_cards)}
                sub={s.total_cards
                    ? `${Math.round(((s.scheduled_cards || 0) / s.total_cards) * 100)}% ${t("card_fleet.kpi.of_fleet")}`
                    : ""}
                testId="kpi-scheduled-cards"
                insightId="fleet.kpi.scheduled-cards"
            />
            <KpiTile
                icon={MapPin}
                accent="#34d399"
                label={t("card_fleet.kpi.total_locations")}
                value={loading ? "…" : fmt(s.total_locations)}
                testId="kpi-total-locations"
                insightId="fleet.kpi.locations"
            />
            <KpiTile
                icon={Activity}
                accent="#22d3ee"
                label={t("card_fleet.kpi.taps_today")}
                value={loading ? "…" : fmt(s.taps_today)}
                testId="kpi-taps-today"
                insightId="fleet.kpi.taps-today"
            />
            <KpiTile
                icon={Clock}
                accent="#f59e0b"
                label={t("card_fleet.kpi.upcoming_changes")}
                value={loading ? "…" : fmt(s.upcoming_changes)}
                sub={typeof s.fleet_health_pct === "number"
                    ? t("card_fleet.kpi.fleet_health_pct", { pct: s.fleet_health_pct })
                    : ""}
                testId="kpi-upcoming-changes"
                insightId="fleet.kpi.upcoming-changes"
            />
        </div>
    );
}
